import { posthog } from "posthog-js";
import type {
  AnalyticsConfig,
  AnalyticsValue,
  IdentitySetOnceProps,
  IdentitySetProps,
  OrganizationContextProps,
} from "./types.js";

let initialized = false;
let activeConfig: AnalyticsConfig | null = null;

export function initPostHog(config: AnalyticsConfig): void {
  if (initialized) return;
  activeConfig = config;
  if (config.disabled) {
    initialized = true;
    return;
  }
  if (!config.posthogKey || typeof window === "undefined") {
    initialized = true;
    return;
  }
  posthog.init(config.posthogKey, {
    api_host: config.posthogHost ?? "https://us.i.posthog.com",
    person_profiles: "identified_only",
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    disable_session_recording: !config.sessionRecording,
    // Session replay masking. Inert unless `sessionRecording` is on (recording
    // is disabled otherwise). Consumer apps capture heavy PII (DOB, SSN, bank,
    // addresses, comp), so replays MUST mask every input value by default plus
    // any element explicitly tagged `data-sensitive`. This is the shared mask
    // contract the analytics rule (`analytics.mdc`) relies on — apps opt fields
    // in with `data-sensitive` / `type="password"`.
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-sensitive]",
    },
    sanitize_properties: (properties) => {
      // PostHog's send-time stripping is the second layer of defence
      // (call-site `stripPII` is the first). Drop top-level email-ish keys.
      const blocked = new Set([
        "email",
        "phone",
        "ssn",
        "first_name",
        "last_name",
        "name",
        "dob",
        "date_of_birth",
        "address",
      ]);
      const out: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(properties)) {
        if (blocked.has(k)) continue;
        out[k] = v;
      }
      return out;
    },
  });
  // Tag every PostHog event (including `$pageview` / `$identify` / autocapture)
  // with `app_name` — the property the rest of the org segments apps by. This
  // is the super-property counterpart to the explicit `app_name` the wrapper
  // adds per `track()` call.
  posthog.register({ app_name: config.platform });
  initialized = true;
}

export function captureEvent(event: string, props: Readonly<Record<string, AnalyticsValue>>): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  posthog.capture(event, props);
}

export function identify(
  userId: string,
  setProps?: IdentitySetProps,
  setOnceProps?: IdentitySetOnceProps,
  email?: string,
): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  // The dedicated `email` argument is the ONE sanctioned channel for an
  // email to land on a Person profile (`$set.email`).
  const augmentedSet =
    email !== undefined ? { ...(setProps ?? {}), email } : (setProps ?? undefined);
  posthog.identify(userId, augmentedSet, setOnceProps);
}

export function setGroupOrganization(
  organizationId: string,
  props: OrganizationContextProps,
): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  posthog.group("organization", organizationId, props);
}

export function setGroupPartner(partnerId: string): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  posthog.group("partner", partnerId, {});
}

export function aliasIds(alias: string, distinctId: string): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  posthog.alias(alias, distinctId);
}

export function reset(): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  posthog.reset();
}

export function capturePageview(path: string, search: string): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.posthogKey) return;
  posthog.capture("$pageview", {
    $current_url: `${window.location.origin}${path}${search}`,
  });
}

export function pushToDataLayer(
  event: string,
  props: Readonly<Record<string, AnalyticsValue>>,
): void {
  if (!initialized || activeConfig?.disabled) return;
  if (typeof window === "undefined") return;
  if (!activeConfig?.gtmId) return;
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> };
  w.dataLayer = w.dataLayer ?? [];
  w.dataLayer.push({ event, ...props });
}

export function __reset_for_test(): void {
  initialized = false;
  activeConfig = null;
}

export function __get_config_for_test(): AnalyticsConfig | null {
  return activeConfig;
}
